/**
 * `TransferChannel` — the INJECTABLE abstraction on which the whole
 * protocol runs. A channel transports two kinds of message: text (JSON control) and
 * binary (`ArrayBuffer`, the chunks). It also exposes the backpressure signal
 * (`bufferedAmount` + low threshold) so the sender does not blow up memory.
 *
 * In production an `RTCDataChannel` implements it (see `dataChannelAdapter`).
 * In the tests an in-memory loopback pair implements it (`createLoopbackPair`)
 * with SIMULATED backpressure and the ability to "cut" the channel midway — so the
 * COMPLETE protocol is tested (chunking, resumption, integrity, progress)
 * without real WebRTC or a browser.
 */

export interface TransferChannel {
  /** Sends a message (control text or binary data). */
  send: (data: string | ArrayBuffer) => void;
  /** Bytes queued not yet drained to the peer (backpressure). */
  bufferedAmount: () => number;
  /** Registers the incoming message handler. */
  onMessage: (handler: (data: string | ArrayBuffer) => void) => void;
  /** Fires when the buffer drops below the threshold (resume pumping). */
  onBufferedLow: (handler: () => void) => void;
  /** Fires when the channel closes/falls. */
  onClose: (handler: () => void) => void;
  /** Threshold below which there is considered to be room to send. */
  readonly bufferedLowThreshold: number;
  /** Closes the channel. Idempotent. */
  close: () => void;
}

/** Adapts a real `RTCDataChannel` to `TransferChannel`. */
export function dataChannelAdapter(
  dc: RTCDataChannel,
  bufferedLowThreshold = 256 * 1024,
): TransferChannel {
  dc.binaryType = "arraybuffer";
  dc.bufferedAmountLowThreshold = bufferedLowThreshold;
  return {
    bufferedLowThreshold,
    send(data) {
      // Narrow to resolve the RTCDataChannel.send overloads without a cast.
      if (typeof data === "string") {
        dc.send(data);
      } else {
        dc.send(data);
      }
    },
    bufferedAmount() {
      return dc.bufferedAmount;
    },
    onMessage(handler) {
      dc.addEventListener("message", (event: MessageEvent<unknown>) => {
        const { data } = event;
        if (typeof data === "string" || data instanceof ArrayBuffer) {
          handler(data);
        }
      });
    },
    onBufferedLow(handler) {
      dc.addEventListener("bufferedamountlow", () => {
        handler();
      });
    },
    onClose(handler) {
      dc.addEventListener("close", () => {
        handler();
      });
    },
    close() {
      try {
        dc.close();
      } catch {
        /* already closed */
      }
    },
  };
}

interface LoopbackOptions {
  /** Capacity of the simulated buffer; when exceeded, `bufferedAmount` grows. */
  capacity?: number;
  /** Simulated delivery latency (ms of `setTimeout`); 0 = microtask. */
  latencyMs?: number;
}

interface Endpoint extends TransferChannel {
  /** Forces the channel close (simulates a network cut). */
  drop: () => void;
}

/**
 * Pair of loopback channels connected to each other. What one side `send`s, the other
 * receives. It simulates backpressure: `bufferedAmount` rises on enqueue and drops on
 * drain (on the next tick), firing `onBufferedLow`. `drop()` cuts
 * both sides to test resumption after a fall.
 */
export function createLoopbackPair(options: LoopbackOptions = {}): [Endpoint, Endpoint] {
  const capacity = options.capacity ?? 1024 * 1024;
  const latencyMs = options.latencyMs ?? 0;

  interface Side {
    messageHandler?: (data: string | ArrayBuffer) => void;
    bufferedLowHandler?: () => void;
    closeHandler?: () => void;
    buffered: number;
    closed: boolean;
  }

  const a: Side = { buffered: 0, closed: false };
  const b: Side = { buffered: 0, closed: false };
  const threshold = Math.max(1, Math.floor(capacity / 4));

  function makeEndpoint(self: Side, peer: Side): Endpoint {
    function deliver(data: string | ArrayBuffer, size: number): void {
      const flush = () => {
        self.buffered = Math.max(0, self.buffered - size);
        if (!peer.closed) {
          peer.messageHandler?.(data);
        }
        if (self.buffered <= threshold) {
          self.bufferedLowHandler?.();
        }
      };
      if (latencyMs > 0) {
        setTimeout(flush, latencyMs);
      } else {
        queueMicrotask(flush);
      }
    }
    const endpoint: Endpoint = {
      bufferedLowThreshold: threshold,
      send(data) {
        if (self.closed || peer.closed) {
          return;
        }
        const size = typeof data === "string" ? data.length : data.byteLength;
        self.buffered += size;
        deliver(data, size);
      },
      bufferedAmount() {
        return self.buffered;
      },
      onMessage(handler) {
        self.messageHandler = handler;
      },
      onBufferedLow(handler) {
        self.bufferedLowHandler = handler;
      },
      onClose(handler) {
        self.closeHandler = handler;
      },
      close() {
        if (self.closed) {
          return;
        }
        self.closed = true;
        queueMicrotask(() => peer.closeHandler?.());
      },
      drop() {
        if (self.closed && peer.closed) {
          return;
        }
        self.closed = true;
        peer.closed = true;
        queueMicrotask(() => {
          self.closeHandler?.();
          peer.closeHandler?.();
        });
      },
    };
    return endpoint;
  }

  return [makeEndpoint(a, b), makeEndpoint(b, a)];
}
